import { flow } from '../../../../util/flow';
import { useMemo } from 'react';
import type { MdComponentIndexProps } from '../type';
import { embedCodingHandler } from './handler/coding';
import { embedSocialHandler } from './handler/social';
import { embedVideoHandler } from './handler/video';
import type { EmbedADT, EmbedComponent, EmbedProps } from './type';

function fallback(props: EmbedProps) {
  return <div className="w-full" data-first={props['data-first']} data-last={props['data-last']} />;
}

function fallbackHandler(input: EmbedADT): EmbedComponent {
  // 已经被上游处理过就直接原样输出
  if (!(input instanceof URL)) return input;

  return fallback;
}

const handler = flow(
  function video(input: EmbedADT) {
    // 已经被上游处理过就直接原样输出
    if (!(input instanceof URL)) return input;
    const children = embedVideoHandler(input);
    if (children instanceof URL) return input;
    return (props: EmbedProps) => {
      const { width = '', height = '' } = props;
      return (
        <div
          className={'w-full'}
          style={
            {
              width: width || undefined,
              '--app-markdown-video-ratio': height ? height : '56.25%',
            } as any
          }
          data-first={props['data-first']}
          data-last={props['data-last']}
        >
          {children}
        </div>
      );
    };
  },
  embedCodingHandler,
  embedSocialHandler,
  // fallback iframe
  fallbackHandler,
);

export function MarkdownEmbed(
  props: {
    src?: string;
    width?: string;
    height?: string;
    ratio?: string; // 百分比默认是 56.25% (9 : 16)，作为高的默认值
  } & MdComponentIndexProps,
) {
  const { src = '' } = props;

  const url = useMemo(() => {
    try {
      const res = new URL(src.startsWith('//') ? 'https:' + src : src);

      if (res.protocol === 'tcplayer:') {
        // 腾讯云超级播放器
        // 还要注入 script ，link

        return null;
      }

      if (res.protocol === 'http:' || res.protocol === 'https:') {
        return res;
      }

      // 不支持其他协议

      return null;
    } catch (e) {
      return null;
    }
  }, [src]);

  const ComponentF = useMemo(() => (url ? handler(url) : fallback), [url]);
  return <ComponentF {...props} />;
}

MarkdownEmbed.attributes = ['src', 'width', 'height', 'size'];
